import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CDTAndCustomProcedureSelect, {
  CDTAndCustomProcedureSelectHandle,
  CDTAndCustomProcedureValue,
} from "../../../../components/CDTAndCustomProcedureSelect";
import { EditableProcedureData } from "../../../../types/editableTreatmentPlan";
import { bundleMemberCodes } from "../../../../utils/treatmentPlanAppointmentUtils";
import { TreatmentStepEditChips } from "../TreatmentStepChips";
import {
  EditProcedureCdtCell,
  EditAddCodeButtonWrapper,
} from "./EditTreatmentAppointment.styled";

export interface EditProcedureCdtFieldProps {
  appointmentId: number;
  procedureIndex: number;
  procedure: EditableProcedureData;
  onCdtChange: (
    value: CDTAndCustomProcedureValue,
    allCdtCodes: string[],
    allDescriptions: string[],
    totalFee: number,
    totalDurationMinutes: number,
  ) => void;
}

const EditProcedureCdtField: React.FC<EditProcedureCdtFieldProps> = ({
  appointmentId,
  procedureIndex,
  procedure,
  onCdtChange,
}) => {
  const { t } = useTranslation();
  const selectRef = useRef<CDTAndCustomProcedureSelectHandle>(null);
  const [open, setOpen] = useState(false);

  // A bundle is atomic: its composition lives in the Custom Procedure settings, so
  // its codes are shown as one scope and dropped together, never one by one (OP-665).
  const bundleCodes = bundleMemberCodes(procedure);
  const chipValues = Array.from(new Set(procedure.cdtCodes));

  const handleOpenDropdown = () => {
    setOpen(true);
  };

  const handleRemoveCode = (codeToRemove: string) => {
    if (!procedure.cdtCodes.includes(codeToRemove)) return;
    selectRef.current?.removeCode(codeToRemove);
  };

  // Delegated so fee/duration are recalculated by the same path as the dropdown.
  const handleRemoveBundle = () => {
    selectRef.current?.removeCustomProcedure();
  };

  return (
    <EditProcedureCdtCell>
      <EditAddCodeButtonWrapper>
        <TreatmentStepEditChips
          values={chipValues}
          onRemove={(code) => handleRemoveCode(code)}
          onAddClick={handleOpenDropdown}
          addButtonVariant="code"
          addButtonLabel={t("editTreatmentPlan.addCode")}
          group={
            bundleCodes.length
              ? {
                  values: bundleCodes,
                  onRemove: handleRemoveBundle,
                  reason: t("editTreatmentPlan.bundleCodeLocked"),
                  removeLabel: t("editTreatmentPlan.removeCustomProcedure"),
                }
              : undefined
          }
        />
        <CDTAndCustomProcedureSelect
          ref={selectRef}
          hideTrigger
          open={open}
          onOpenChange={setOpen}
          name={`cdt-${appointmentId}-${procedureIndex}`}
          value={{
            cdtCodes: procedure.cdtCodes,
            customProcedureId: procedure.customProcedureId,
          }}
          onChange={onCdtChange}
          placeholder={t("editTreatmentPlan.cdtCodesPlaceholder")}
        />
      </EditAddCodeButtonWrapper>
    </EditProcedureCdtCell>
  );
};

export default EditProcedureCdtField;
